import {css, StyleSheet} from 'aphrodite';
import React, {MouseEvent} from 'react';

import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {getFeatureStatus} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {StorableSummary, StorableWithPermissions, ValidationStateInfo} from 'constants/SampleConstants';
import {StorableWithValidContentsInfo} from 'constants/SampleStatusConstants';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import StorableValidationStatusIcon from 'inventory/location-validation/StorableValidationStatusIcon';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import SampleStatusLabel from 'inventory/sample-status/SampleStatusLabel';
import * as StorableContextMenu from 'samples/sidepanel/StorableContextMenu';
import * as StorableActions from 'samples/StorableActions';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {apiIdToIconName} from 'util/APIUtil';

import SidepanelItem from './SidepanelItem';

interface StorableSidepanelTitleProps {
  storable: StorableSummary;
}

function StorableSidepanelTitle({storable}: StorableSidepanelTitleProps): JSX.Element {
  return (
    <span>
      <span className="sidepanel-primaryLabel">{storable.name}</span>
      {storable.barcode && <span className="sidepanel-secondaryLabel"> {storable.barcode}</span>}
    </span>
  );
}

interface StorableSidepanelItemProps {
  isActive: boolean;
  isCompact: boolean;
  storable: StorableWithPermissions;
  openResult: (storable: StorableWithPermissions) => void;
  isChild: boolean;
  validContentsInfo?: StorableWithValidContentsInfo;
  validationStateInfo?: ValidationStateInfo;
}

class StorableSidepanelItem extends React.Component<StorableSidepanelItemProps> {
  static defaultProps = {isActive: false};

  handleOpen = (): void => {
    this.props.openResult(this.props.storable);
  };

  private handleOpenContextMenu = (e: MouseEvent): void => {
    StorableContextMenu.open(e.nativeEvent, this.props.storable);
  };

  private handleClickStar = (): void => {
    if (this.props.storable.starred) {
      StorableActions.unstarStorables([this.props.storable.id]);
    } else {
      StorableActions.starStorables([this.props.storable.id]);
    }
  };

  render(): JSX.Element {
    const {isActive, isCompact, storable} = this.props;
    const {prefix, date} = FileBrowserUtil.getResultDate(storable, {fuzzy: true, compact: isCompact});
    const subtitle = `${isCompact ? '' : prefix}${date}`;
    const featureStatus = getFeatureStatus({
      feature: StandardFeature.OPEN,
      item: storable,
    });
    return (
      <SidepanelItem
        active={isActive}
        className="sidepanelItem--noIcon"
        faded={Boolean(storable.archiveRecord) || !featureStatus.enabled}
        handleClickStar={this.handleClickStar}
        // TODO(T9662): Render storable schema icon instead.
        icon={<BenchlingIcon icon={apiIdToIconName(this.props.storable.id)} />}
        isCompact={isCompact}
        onContextMenu={this.handleOpenContextMenu}
        onClick={this.handleOpen}
        showStar={true}
        starred={storable.starred}
        sampleStatusIcon={
          <>
            <SampleStatusLabel
              item={{...storable, ...this.props.validContentsInfo}}
              className={css(styles.sampleStatusLabel)}
              includeAuthorization={hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1)}
              includeAvailability={true}
              includeExpiration={BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE}
              includeMessage={false}
              renderAllStatuses={false}
            />
            <StorableValidationStatusIcon
              storable={{...storable, ...this.props.validationStateInfo}}
              className="u-mini-spacing-left"
            />
          </>
        }
        subTitle={subtitle}
        title={{text: storable.name, node: <StorableSidepanelTitle storable={storable} />}}
        tooltipText={featureStatus.enabled ? '' : featureStatus.userMessage}
        isExpandable={false}
        isChild={this.props.isChild}
      />
    );
  }
}

export default StorableSidepanelItem;

const styles = StyleSheet.create({
  sampleStatusLabel: {
    marginLeft: '12px',
  },
});
